/**
 * TECNILAND - Validadores de formularios
 * Funciones de validación reutilizables para login y registro
 */

/**
 * Valida el formato del username
 * @param {string} username 
 * @returns {Object} { valid: boolean, error: string }
 */
export function validateUsername(username) {
  if (!username || username.trim() === '') {
    return { valid: false, error: 'El nombre de usuario es requerido' };
  }
  
  if (username.length < 3) {
    return { valid: false, error: 'Mínimo 3 caracteres' };
  }
  
  if (username.length > 16) {
    return { valid: false, error: 'Máximo 16 caracteres' };
  }
  
  const usernameRegex = /^[a-zA-Z0-9_]+$/;
  if (!usernameRegex.test(username)) {
    return { valid: false, error: 'Solo letras, números y guiones bajos' };
  }
  
  return { valid: true, error: '' };
}

/**
 * Valida el formato del email
 * @param {string} email 
 * @returns {Object} { valid: boolean, error: string }
 */
export function validateEmail(email) {
  if (!email || email.trim() === '') {
    return { valid: false, error: 'El email es requerido' };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Formato de email inválido' };
  }
  
  if (email.length > 255) {
    return { valid: false, error: 'Email demasiado largo' };
  }
  
  return { valid: true, error: '' };
}

/**
 * Valida la contraseña y retorna nivel de fortaleza
 * @param {string} password 
 * @returns {Object} { valid: boolean, error: string, strength: number, checks: Object }
 */
export function validatePassword(password) {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(password)
  };
  
  // Calcular fortaleza (0-4)
  const strength = Object.values(checks).filter(Boolean).length;
  
  if (!password || password.trim() === '') {
    return { 
      valid: false, 
      error: 'La contraseña es requerida', 
      strength: 0,
      checks 
    };
  }
  
  if (!checks.minLength) {
    return { 
      valid: false, 
      error: 'Mínimo 8 caracteres', 
      strength,
      checks 
    };
  }
  
  // Para registro, se recomienda todos los checks pero no son obligatorios
  return { 
    valid: true, 
    error: '', 
    strength,
    checks 
  };
}

/**
 * Valida que las contraseñas coincidan
 * @param {string} password 
 * @param {string} confirmPassword 
 * @returns {Object} { valid: boolean, error: string }
 */
export function validatePasswordMatch(password, confirmPassword) {
  if (!confirmPassword) {
    return { valid: false, error: 'Confirma tu contraseña' };
  }
  
  if (password !== confirmPassword) {
    return { valid: false, error: 'Las contraseñas no coinciden' };
  }
  
  return { valid: true, error: '' };
}

/**
 * Valida y formatea el Access Key
 * @param {string} accessKey 
 * @returns {Object} { valid: boolean, error: string, formatted: string }
 */
export function validateAccessKey(accessKey) {
  if (!accessKey || accessKey.trim() === '') {
    return { valid: false, error: 'La llave de acceso es requerida', formatted: '' };
  }
  
  // Remover espacios y convertir a mayúsculas
  const formatted = accessKey.toUpperCase().replace(/\s/g, '');
  
  // Formato esperado: TECNI-XXXX-XXXX-XXXX-XXXX (25 caracteres con guiones)
  const accessKeyRegex = /^TECNI-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}$/;
  
  if (!accessKeyRegex.test(formatted)) {
    return { 
      valid: false, 
      error: 'Formato: TECNI-XXXX-XXXX-XXXX-XXXX', 
      formatted 
    };
  }
  
  return { valid: true, error: '', formatted };
}

/**
 * Auto-formatea el Access Key mientras el usuario escribe
 * @param {string} value - Valor actual del input
 * @returns {string} Valor formateado
 */
export function formatAccessKeyInput(value) {
  // Remover todo excepto letras y números
  let cleaned = value.toUpperCase().replace(/[^A-F0-9]/g, '');
  
  // Si ya tiene el prefijo TECNI, manejarlo especialmente
  if (value.toUpperCase().startsWith('TECNI')) {
    // Mantener TECNI y formatear el resto
    const afterPrefix = value.toUpperCase().replace('TECNI', '').replace(/[^A-F0-9]/g, '');
    
    let formatted = 'TECNI';
    for (let i = 0; i < afterPrefix.length && i < 16; i++) {
      if (i % 4 === 0) {
        formatted += '-';
      }
      formatted += afterPrefix[i];
    }
    return formatted;
  }
  
  // Si el usuario escribe los caracteres sin TECNI-
  // Añadir formato automáticamente después de cierta longitud
  if (cleaned.length > 0) {
    let formatted = 'TECNI-';
    for (let i = 0; i < cleaned.length && i < 16; i++) {
      if (i > 0 && i % 4 === 0) {
        formatted += '-';
      }
      formatted += cleaned[i];
    }
    return formatted;
  }
  
  return value.toUpperCase();
}

/**
 * Obtiene el mensaje de error según el código del backend
 * @param {string} code - Código de error del backend
 * @param {string} defaultMessage - Mensaje por defecto
 * @returns {string} Mensaje de error legible
 */
export function getErrorMessage(code, defaultMessage = 'Ha ocurrido un error') {
  const errorMessages = {
    // Errores de registro
    'INVALID_USERNAME': 'El nombre de usuario no es válido',
    'INVALID_EMAIL': 'El email no es válido',
    'INVALID_PASSWORD': 'La contraseña no cumple los requisitos',
    'USERNAME_TAKEN': 'Este nombre de usuario ya está en uso',
    'EMAIL_TAKEN': 'Este email ya está registrado',
    'INVALID_KEY_FORMAT': 'Formato de llave inválido. Debe ser: TECNI-XXXX-XXXX-XXXX-XXXX',
    'KEY_NOT_FOUND': 'La llave de acceso no es válida o expiró',
    'KEY_ALREADY_USED': 'Esta llave de acceso ya fue utilizada',
    'ACCESS_KEY_REQUIRED': 'La llave de acceso es obligatoria',
    
    // Errores de login
    'USERNAME_REQUIRED': 'El nombre de usuario es requerido',
    'PASSWORD_REQUIRED': 'La contraseña es requerida',
    'INVALID_CREDENTIALS': 'Credenciales inválidas',
    
    // Errores de token
    'TOKEN_REQUIRED': 'Sesión no válida. Por favor inicia sesión nuevamente',
    'INVALID_TOKEN': 'Sesión expirada. Por favor inicia sesión nuevamente',
    
    // Errores de conexión
    'CONNECTION_ERROR': 'Error de conexión con el servidor. Verifica tu conexión a internet',
    'SERVER_OFFLINE': 'El servidor no está disponible en este momento',
    'INTERNAL_ERROR': 'Error interno del servidor. Intenta nuevamente más tarde'
  };
  
  return errorMessages[code] || defaultMessage;
}

const validators = {
  validateUsername,
  validateEmail,
  validatePassword,
  validatePasswordMatch,
  validateAccessKey,
  formatAccessKeyInput,
  getErrorMessage
};

export default validators;
